// 遊戲結束畫面 - Victory Screen
// 顯示勝敗結果、分數、統計摘要

import { useState, useEffect, useMemo } from 'react';
import { useUIStore, useGameStore } from '../../store';
import { calculateBothScores, countUnits } from '../../core/rules/ScoreCalculator';
import type { GameEvent } from '../../types/events';

/**
 * 統計資料結構
 */
interface GameStats {
  totalTurns: number;
  player1: PlayerStats;
  player2: PlayerStats;
}

interface PlayerStats {
  name: string;
  faction: string;
  score: number;
  unitsDestroyed: number;     // 此玩家摧毀的對方單位數
  unitsLost: number;          // 此玩家失去的單位數
  unitsSummoned: number;      // 此玩家召喚的單位數
  spellsCast: number;         // 此玩家使用的魔法數
  unitsOnField: number;       // 結束時場上單位數
  cardsDrawn: number;         // 抽牌數
}

/**
 * 從事件日誌提取統計資料
 */
function extractStats(events: GameEvent[], gameState: NonNullable<ReturnType<typeof useGameStore.getState>['gameState']>, cardDefinitions: Map<string, unknown>): GameStats {
  const stats: GameStats = {
    totalTurns: gameState.turnNumber,
    player1: {
      name: gameState.player1.name || '玩家',
      faction: gameState.player1.faction || '',
      score: 0,
      unitsDestroyed: 0,
      unitsLost: 0,
      unitsSummoned: 0,
      spellsCast: 0,
      unitsOnField: 0,
      cardsDrawn: 0,
    },
    player2: {
      name: gameState.player2.name || '對手',
      faction: gameState.player2.faction || '',
      score: 0,
      unitsDestroyed: 0,
      unitsLost: 0,
      unitsSummoned: 0,
      spellsCast: 0,
      unitsOnField: 0,
      cardsDrawn: 0,
    },
  };

  // 計算分數
  const scores = calculateBothScores(
    gameState.battlefield,
    cardDefinitions as Map<string, import('../../types').CardDefinition>
  );
  stats.player1.score = scores.player1Score + (gameState.player1.reward ?? 0);
  stats.player2.score = scores.player2Score + (gameState.player2.reward ?? 0);

  // 計算場上單位數
  stats.player1.unitsOnField = countUnits(gameState.battlefield, 'player1');
  stats.player2.unitsOnField = countUnits(gameState.battlefield, 'player2');

  // 從事件日誌提取統計
  for (const event of events) {
    const payload = event.payload as Record<string, unknown>;
    const isP1 = event.playerId === 'player1';
    const target = isP1 ? stats.player1 : stats.player2;

    switch (event.type) {
      case 'CARD_SUMMON':
        target.unitsSummoned++;
        break;
      case 'CARD_DRAW':
        target.cardsDrawn++;
        break;
      case 'SPELL_CAST':
        target.spellsCast++;
        break;
      case 'UNIT_DESTROYED': {
        // 被摧毀的單位屬於誰
        const ownerId = (payload.ownerId as string) || event.playerId;
        if (ownerId === 'player1') {
          stats.player1.unitsLost++;
          stats.player2.unitsDestroyed++;
        } else {
          stats.player2.unitsLost++;
          stats.player1.unitsDestroyed++;
        }
        break;
      }
    }
  }

  return stats;
}

/**
 * 勝敗原因文字
 */
function getWinReasonText(gameState: NonNullable<ReturnType<typeof useGameStore.getState>['gameState']>, winnerId: string | null, stats: GameStats): string {
  if (!winnerId) return '雙方分數相同，勢均力敵！';

  const winner = winnerId === 'player1' ? stats.player1 : stats.player2;
  const loser = winnerId === 'player1' ? stats.player2 : stats.player1;
  const scoreDiff = winner.score - loser.score;

  if (gameState.countdownTurns !== null) {
    // 倒數結束
    if (scoreDiff >= 10) return `以 ${String(scoreDiff)} 分的壓倒性優勢獲勝！`;
    if (scoreDiff >= 5) return `憑藉 ${String(scoreDiff)} 分的優勢穩穩拿下勝利`;
    if (scoreDiff >= 1) return `僅以 ${String(scoreDiff)} 分之差險勝，好一場激戰！`;
    if (scoreDiff === 0) return '分數相同，後手玩家獲勝！';
  }

  return `最終分數 ${String(winner.score)} vs ${String(loser.score)}`;
}

/**
 * 動畫用的數字遞增 hook
 */
function useAnimatedNumber(target: number, duration: number, delay: number): number {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const startTime = Date.now();
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // easeOutQuart
        const eased = 1 - Math.pow(1 - progress, 4);
        setCurrent(Math.round(target * eased));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, delay);

    return () => { clearTimeout(timeout); };
  }, [target, duration, delay]);

  return current;
}

/**
 * Victory Screen 主元件
 */
export function VictoryScreen() {
  const { navigateTo } = useUIStore();
  const { gameState, cardDefinitions, eventLogger, resetGame, gameMode } = useGameStore();
  const [showStats, setShowStats] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);

  // 提取遊戲統計
  const stats = useMemo(() => {
    if (!gameState) return null;
    const events = eventLogger?.getEvents() ?? [];
    return extractStats(events, gameState, cardDefinitions);
  }, [gameState, eventLogger, cardDefinitions]);

  // 勝負判定
  const winnerId = gameState?.winnerId ?? null;
  const isTie = !winnerId;
  const isPlayerWin = winnerId === 'player1';

  // 動畫時序
  useEffect(() => {
    const timers = [
      setTimeout(() => { setAnimationPhase(1); }, 200),   // 標題淡入
      setTimeout(() => { setAnimationPhase(2); }, 800),   // 分數顯示
      setTimeout(() => { setAnimationPhase(3); }, 1500),  // 原因文字
      setTimeout(() => { setAnimationPhase(4); }, 2200),  // 按鈕出現
      setTimeout(() => { setShowStats(true); }, 2800),    // 統計面板
    ];
    return () => { timers.forEach((t) => { clearTimeout(t); }); };
  }, []);

  // 動畫分數
  const p1AnimScore = useAnimatedNumber(stats?.player1.score ?? 0, 1200, 800);
  const p2AnimScore = useAnimatedNumber(stats?.player2.score ?? 0, 1200, 800);

  const isOnline = gameMode === 'online';

  const handleRematch = () => {
    resetGame();
    navigateTo(isOnline ? 'lobby' : 'mainMenu');
  };

  const handleReturn = () => {
    resetGame();
    navigateTo('mainMenu');
  };

  if (!gameState || !stats) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>遊戲結束</h1>
        <button style={styles.primaryButton} onClick={() => { navigateTo('mainMenu'); }}>
          返回主選單
        </button>
      </div>
    );
  }

  const winReason = getWinReasonText(gameState, winnerId, stats);

  return (
    <div style={styles.container}>
      {/* 背景粒子效果 */}
      <div style={styles.bgOverlay} />

      {/* 主標題 - 勝敗結果 */}
      <div style={{
        ...styles.resultSection,
        opacity: animationPhase >= 1 ? 1 : 0,
        transform: animationPhase >= 1 ? 'translateY(0) scale(1)' : 'translateY(-30px) scale(0.8)',
        transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>
        <div style={{
          ...styles.resultEmoji,
          fontSize: isTie ? '72px' : '80px',
        }}>
          {isTie ? '🤝' : isPlayerWin ? '👑' : '💀'}
        </div>
        <h1 style={{
          ...styles.resultTitle,
          color: isTie ? '#a0a0a0' : isPlayerWin ? '#ffd700' : '#ff4444',
          textShadow: isTie
            ? '0 0 30px rgba(160,160,160,0.5)'
            : isPlayerWin
              ? '0 0 40px rgba(255,215,0,0.6), 0 0 80px rgba(255,215,0,0.3)'
              : '0 0 30px rgba(255,68,68,0.5)',
        }}>
          {isTie ? '平局' : isPlayerWin ? '勝利！' : '敗北...'}
        </h1>
      </div>

      {/* 分數對比 */}
      <div style={{
        ...styles.scoreSection,
        opacity: animationPhase >= 2 ? 1 : 0,
        transform: animationPhase >= 2 ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.5s ease-out',
      }}>
        <div style={styles.scoreCard}>
          <div style={{
            ...styles.scoreSide,
            borderColor: winnerId === 'player1' ? '#ffd700' : '#444',
          }}>
            <div style={styles.playerLabel}>{stats.player1.name}</div>
            <div style={styles.factionLabel}>{stats.player1.faction}</div>
            <div style={{
              ...styles.scoreValue,
              color: winnerId === 'player1' ? '#ffd700' : '#ffffff',
            }}>
              {String(p1AnimScore)}
            </div>
            <div style={styles.scoreLabel}>分</div>
          </div>

          <div style={styles.vsSection}>
            <div style={styles.vsText}>VS</div>
          </div>

          <div style={{
            ...styles.scoreSide,
            borderColor: winnerId === 'player2' ? '#ffd700' : '#444',
          }}>
            <div style={styles.playerLabel}>{stats.player2.name}</div>
            <div style={styles.factionLabel}>{stats.player2.faction}</div>
            <div style={{
              ...styles.scoreValue,
              color: winnerId === 'player2' ? '#ffd700' : '#ffffff',
            }}>
              {String(p2AnimScore)}
            </div>
            <div style={styles.scoreLabel}>分</div>
          </div>
        </div>
      </div>

      {/* 勝敗原因 */}
      <div style={{
        ...styles.reasonSection,
        opacity: animationPhase >= 3 ? 1 : 0,
        transform: animationPhase >= 3 ? 'translateY(0)' : 'translateY(15px)',
        transition: 'all 0.5s ease-out',
      }}>
        <p style={styles.reasonText}>{winReason}</p>
        <p style={styles.turnsText}>共進行了 {String(stats.totalTurns)} 回合</p>
      </div>

      {/* 統計面板 */}
      {showStats && (
        <div style={{
          ...styles.statsSection,
          opacity: 1,
          transition: 'opacity 0.5s ease-out',
        }}>
          <div style={styles.statsGrid}>
            <StatRow
              label="召喚單位"
              p1Value={stats.player1.unitsSummoned}
              p2Value={stats.player2.unitsSummoned}
              winnerId={winnerId}
            />
            <StatRow
              label="摧毀敵方"
              p1Value={stats.player1.unitsDestroyed}
              p2Value={stats.player2.unitsDestroyed}
              winnerId={winnerId}
            />
            <StatRow
              label="失去單位"
              p1Value={stats.player1.unitsLost}
              p2Value={stats.player2.unitsLost}
              winnerId={winnerId}
              invertHighlight
            />
            <StatRow
              label="場上單位"
              p1Value={stats.player1.unitsOnField}
              p2Value={stats.player2.unitsOnField}
              winnerId={winnerId}
            />
            <StatRow
              label="抽牌數"
              p1Value={stats.player1.cardsDrawn}
              p2Value={stats.player2.cardsDrawn}
              winnerId={winnerId}
            />
            {(stats.player1.spellsCast > 0 || stats.player2.spellsCast > 0) && (
              <StatRow
                label="魔法施放"
                p1Value={stats.player1.spellsCast}
                p2Value={stats.player2.spellsCast}
                winnerId={winnerId}
              />
            )}
          </div>
        </div>
      )}

      {/* 按鈕區 */}
      <div style={{
        ...styles.buttonSection,
        opacity: animationPhase >= 4 ? 1 : 0,
        transform: animationPhase >= 4 ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.4s ease-out',
      }}>
        <button
          style={styles.primaryButton}
          onClick={handleRematch}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 0 20px rgba(255,215,0,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {isOnline ? '🏠 返回大廳' : '🔄 再來一局'}
        </button>
        <button
          style={styles.secondaryButton}
          onClick={handleReturn}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
          }}
        >
          🏠 返回主選單
        </button>
      </div>
    </div>
  );
}

/**
 * 統計列元件
 */
function StatRow({
  label,
  p1Value,
  p2Value,
  invertHighlight = false,
}: {
  label: string;
  p1Value: number;
  p2Value: number;
  winnerId?: string | null;
  invertHighlight?: boolean;
}) {
  const p1Better = invertHighlight ? p1Value < p2Value : p1Value > p2Value;
  const p2Better = invertHighlight ? p2Value < p1Value : p2Value > p1Value;

  return (
    <div style={styles.statRow}>
      <span style={{
        ...styles.statValue,
        color: p1Better ? '#ffd700' : '#ccc',
        fontWeight: p1Better ? 'bold' : 'normal',
      }}>
        {String(p1Value)}
      </span>
      <span style={styles.statLabel}>{label}</span>
      <span style={{
        ...styles.statValue,
        color: p2Better ? '#ffd700' : '#ccc',
        fontWeight: p2Better ? 'bold' : 'normal',
      }}>
        {String(p2Value)}
      </span>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    position: 'relative',
    overflow: 'hidden',
    background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #0d0d1a 70%, #000000 100%)',
  },

  bgOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.015) 50px, rgba(255,255,255,0.015) 51px)',
    pointerEvents: 'none' as const,
  },

  // 結果區
  resultSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    zIndex: 1,
  },

  resultEmoji: {
    fontSize: '80px',
    lineHeight: 1,
    marginBottom: '8px',
  },

  resultTitle: {
    fontSize: '56px',
    fontWeight: 900,
    margin: 0,
    fontFamily: '"Noto Sans TC", "PingFang TC", sans-serif',
    letterSpacing: '8px',
  },

  // 分數區
  scoreSection: {
    zIndex: 1,
    width: '100%',
    maxWidth: '500px',
    padding: '0 20px',
  },

  scoreCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '16px',
    padding: '20px 12px',
    backdropFilter: 'blur(10px)',
  },

  scoreSide: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    padding: '8px',
    borderRadius: '12px',
    border: '2px solid transparent',
    transition: 'border-color 0.3s',
  },

  playerLabel: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#ffffff',
    fontFamily: '"Noto Sans TC", sans-serif',
  },

  factionLabel: {
    fontSize: '12px',
    color: '#888',
    marginBottom: '4px',
  },

  scoreValue: {
    fontSize: '48px',
    fontWeight: 900,
    lineHeight: 1.1,
    fontFamily: '"Noto Sans TC", monospace',
  },

  scoreLabel: {
    fontSize: '14px',
    color: '#888',
    fontWeight: 500,
  },

  vsSection: {
    padding: '0 12px',
  },

  vsText: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#555',
    letterSpacing: '2px',
  },

  // 原因區
  reasonSection: {
    textAlign: 'center',
    zIndex: 1,
    padding: '0 20px',
  },

  reasonText: {
    fontSize: '18px',
    color: '#cccccc',
    margin: '0 0 4px 0',
    fontFamily: '"Noto Sans TC", sans-serif',
  },

  turnsText: {
    fontSize: '14px',
    color: '#777',
    margin: 0,
  },

  // 統計區
  statsSection: {
    width: '100%',
    maxWidth: '420px',
    padding: '0 20px',
    zIndex: 1,
  },

  statsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '12px',
    padding: '12px 16px',
  },

  statRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },

  statLabel: {
    fontSize: '13px',
    color: '#888',
    textAlign: 'center',
    flex: 1,
    fontFamily: '"Noto Sans TC", sans-serif',
  },

  statValue: {
    fontSize: '16px',
    width: '50px',
    textAlign: 'center',
    fontFamily: 'monospace',
  },

  // 按鈕區
  buttonSection: {
    display: 'flex',
    gap: '12px',
    zIndex: 1,
    marginTop: '8px',
  },

  primaryButton: {
    padding: '14px 32px',
    fontSize: '18px',
    fontWeight: 700,
    color: '#1a1a2e',
    backgroundColor: '#ffd700',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontFamily: '"Noto Sans TC", sans-serif',
    transition: 'all 0.2s ease',
    letterSpacing: '1px',
  },

  secondaryButton: {
    padding: '14px 32px',
    fontSize: '18px',
    fontWeight: 600,
    color: '#cccccc',
    backgroundColor: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '12px',
    cursor: 'pointer',
    fontFamily: '"Noto Sans TC", sans-serif',
    transition: 'all 0.2s ease',
    letterSpacing: '1px',
  },
};
